import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { BarChart3, Workflow, Bot, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const cards = [
    {
        badge: "01 / Analytics",
        icon: BarChart3,
        title: "Predictive Revenue Analytics",
        description:
            "Uncover hidden friction in your pipeline with real-time funnel intelligence, forecast accuracy dashboards and conversion leakage alerts — so every decision is backed by data, not gut feel.",
        span: "lg:col-span-7",
        image: "https://images.pexels.com/photos/27141314/pexels-photo-27141314.jpeg",
        testid: "services-analytics-card",
    },
    {
        badge: "02 / Automations",
        icon: Workflow,
        title: "Hyper-Automated Sales Workflows",
        description:
            "Eliminate manual data entry. Auto-enrich leads, sequence follow-ups and trigger instant meeting bookings while your reps focus on closing.",
        span: "lg:col-span-5",
        image: "https://images.pexels.com/photos/27141307/pexels-photo-27141307.jpeg",
        testid: "services-automation-card",
    },
    {
        badge: "03 / AI Agents",
        icon: Bot,
        title: "Autonomous AI Sales Copilots",
        description:
            "Deploy custom AI agents trained on your highest-performing sales scripts. They qualify leads around the clock, answer objections in real time, and hand off warm prospects to your team — 24/7, without a single missed opportunity.",
        span: "lg:col-span-12",
        image: null,
        testid: "services-ai-card",
    },
];

function TiltCard({ card, index }) {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rx = useSpring(useTransform(my, [0, 1], [4, -4]), { stiffness: 150, damping: 20 });
    const ry = useSpring(useTransform(mx, [0, 1], [-4, 4]), { stiffness: 150, damping: 20 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };

    return (
        <motion.article
            ref={ref}
            data-testid={card.testid}
            onMouseMove={onMove}
            onMouseLeave={() => {
                mx.set(0.5);
                my.set(0.5);
            }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: index * 0.12, ease: EASE }}
            className={`group relative col-span-full ${card.span} rounded-3xl border border-white/10 bg-[#141A26]/60 overflow-hidden hover:border-[#FF3B30]/50 transition-colors duration-500 [perspective:1000px]`}
        >
            {card.image && (
                <div className="absolute inset-0">
                    <img
                        src={card.image}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#141A26] via-[#141A26]/70 to-transparent" />
                </div>
            )}
            {!card.image && (
                <div className="absolute inset-0 opacity-40">
                    <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#FF3B30]/15 blur-[100px] group-hover:bg-[#FF3B30]/25 transition-colors duration-700" />
                </div>
            )}

            <div className="relative z-10 p-8 sm:p-10 flex flex-col h-full min-h-[280px]">
                <div className="flex items-start justify-between">
                    <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold">
                        {card.badge}
                    </span>
                    <card.icon className="w-7 h-7 text-slate-500 group-hover:text-[#FF3B30] transition-colors duration-500" />
                </div>
                <div className="mt-auto pt-16">
                    <h3 className="font-display font-bold text-xl sm:text-2xl lg:text-3xl text-white tracking-tight">
                        {card.title}
                    </h3>
                    <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">
                        {card.description}
                    </p>
                </div>
            </div>
        </motion.article>
    );
}

export default function Services() {
    return (
        <section id="services" data-testid="services-section" className="px-4 sm:px-8 lg:px-16 py-24 lg:py-36">
            <div className="max-w-[1400px] mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14"
                >
                    <div>
                        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-semibold mb-4">
                            What we build for you
                        </p>
                        <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-5xl text-white leading-tight">
                            Three engines.
                            <br />
                            <span className="text-stroke">One revenue machine.</span>
                        </h2>
                    </div>
                    <button
                        data-testid="services-cta-button"
                        onClick={() => scrollToId("#contact")}
                        className="group flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors duration-300 shrink-0"
                    >
                        Build mine
                        <span className="w-9 h-9 rounded-full border border-white/15 group-hover:border-[#FF3B30]/60 group-hover:bg-[#FF3B30]/10 flex items-center justify-center transition-colors duration-300">
                            <ArrowUpRight className="w-4 h-4" />
                        </span>
                    </button>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {cards.map((c, i) => (
                        <TiltCard key={c.testid} card={c} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
